import React, { useEffect, useRef, useState } from "react";
import { SlDrawer } from "@shoelace-style/shoelace/dist/react";
import * as XLSX from "sheetjs-style";
import { toBlob } from "html-to-image";
import { saveAs } from "file-saver";

import API from "../../services/api";
import TableLoader from "../../shared/TableLoader";
import { Toast } from "../../shared/Toast";
import { iconMap, roles } from "../../utils/constants";

/**
 * Sales-facing per-order report, built from the CST's Sales Report Metrics
 * config (see SalesReportMetrics.js) via /order_report_metrics/get-report.
 *
 * Which metric columns exist is decided SERVER-side and published as
 * `data.columns`; a metric the CST disabled is never computed and never reaches
 * the browser. This component renders whatever columns it is given and holds no
 * gating logic of its own.
 */

const FIELD_COLUMNS = [
  { key: "site", label: "Site" },
  { key: "date", label: "Date" },
  { key: "ad", label: "Ad" },
];

const METRIC_LABELS = {
  impressions: "Impressions",
  clicks: "Clicks",
  ctr: "CTR",
  reach: "Reach",
  views: "Views",
};

// Serving Locations comes back as sections, because the two kinds of line item
// are known at different resolutions: Regular areas are derived from location
// targeting, DFP ones are typed by the CST. Each section reads Site | Geo | Ad
// like the day-wise table above it, with Geo where Date would be, and says
// which field columns it has.
const GEO_FIELD_LABELS = {
  site: "Site",
  geo: "Geo",
  ad: "Ad",
};

const geoSections = (data) => data?.geo?.sections || [];

const BAND = { background: "#E8C5C0" };
const CELL = { border: "1px solid #b7b7b7", padding: "6px 10px" };

// Unix seconds -> DD/MM/YYYY, matching the agreed report format.
const formatDMY = (seconds) => {
  if (!seconds) return "-";
  const d = new Date(seconds * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// en-US grouping (483,400) to match the agreed report format - NOT en-IN,
// which would render Indian lakh grouping (4,83,400).
const count = (n) => Number(n || 0).toLocaleString("en-US");

// The API sends CTR as a number, so 0.60 arrives as 0.6 - re-fix the decimals.
const formatMetric = (key, value) => {
  if (value === null || value === undefined) return "-";
  if (key === "ctr") return `${Number(value).toFixed(2)}%`;
  return count(value);
};

// Creative-type slugs (full_video, ...) -> display labels, reusing the app's
// canonical map; fall back to prettifying the slug.
const creativeLabel = (slug) =>
  iconMap?.[slug]?.label ||
  String(slug || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// `ad` arrives as a raw creative_type slug, same as ad_type, so every place
// that shows a field cell has to run it through creativeLabel.
const fieldValue = (key, row) => (key === "ad" ? creativeLabel(row.ad) : row[key]);

// ---- Excel export ----------------------------------------------------------

const XL_BORDER = {
  top: { style: "thin", color: { rgb: "000000" } },
  bottom: { style: "thin", color: { rgb: "000000" } },
  left: { style: "thin", color: { rgb: "000000" } },
  right: { style: "thin", color: { rgb: "000000" } },
};
const XL_BASE = { border: XL_BORDER, alignment: { horizontal: "center", vertical: "center" } };
const XL_BOLD = { ...XL_BASE, font: { bold: true } };
// Same dusty rose as the on-screen Report Fields / Report Metrics band.
const XL_BAND = { ...XL_BOLD, fill: { fgColor: { rgb: "E8C5C0" } } };

const txt = (v, style = XL_BASE) => ({ v: v === null || v === undefined || v === "" ? "-" : v, t: "s", s: style });
const n = (v, style = XL_BASE) => ({ v: Number(v || 0), t: "n", s: { ...style, numFmt: "#,##0" } });
const blank = (style = XL_BASE) => ({ v: "", t: "s", s: style });

// order_id looks like "order/9/2026/1" - slashes are illegal in filenames and
// in Excel sheet names, so strip them.
const safeName = (id) => String(id || "report").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");

function buildWorkbook(data) {
  const metricCols = data.columns || [];
  const colCount = 3 + Math.max(metricCols.length, 1);
  const rows = [];

  // Header block: one label/value pair per row, label merged across the field
  // columns and value across the metric columns. Cleaner in Excel than the
  // stacked single-cell layout used on screen.
  const adType = (data.ad_type || []).map(creativeLabel).join(", ");
  const range =
    data.start_date && data.end_date
      ? `${formatDMY(data.start_date)} to ${formatDMY(data.end_date)}`
      : "-";
  const headerPairs = [
    ["Campaign Details:", data.order_name],
    ["Ad Type:", adType],
    ["Date of Initiative:", data.start_date ? formatDMY(data.start_date) : "-"],
    ["Generated Report Range:", range],
  ];
  for (const [label, value] of headerPairs) {
    const row = [txt(label, XL_BOLD), blank(XL_BOLD), blank(XL_BOLD), txt(value, XL_BOLD)];
    while (row.length < colCount) row.push(blank(XL_BOLD));
    rows.push(row);
  }

  // Band row
  const bandRow = [txt("Report Fields", XL_BAND), blank(XL_BAND), blank(XL_BAND), txt("Report Metrics", XL_BAND)];
  while (bandRow.length < colCount) bandRow.push(blank(XL_BAND));
  rows.push(bandRow);

  // Column headers
  rows.push([
    txt("Site", XL_BOLD),
    txt("Date", XL_BOLD),
    txt("Ad", XL_BOLD),
    ...metricCols.map((k) => txt(METRIC_LABELS[k] || k, XL_BOLD)),
  ]);

  // Data rows. CTR stays a string so it reads "0.64%"; the counts go out as
  // real numbers so Excel can still total them.
  const metricCell = (key, value) =>
    key === "ctr" ? txt(formatMetric(key, value)) : n(value);

  for (const row of data.rows || []) {
    rows.push([
      txt(row.site),
      txt(formatDMY(row.date)),
      txt(creativeLabel(row.ad)),
      ...metricCols.map((k) => metricCell(k, row[k])),
    ]);
  }

  rows.push([
    txt("Grand Total", XL_BOLD),
    txt("-", XL_BOLD),
    txt("-", XL_BOLD),
    ...metricCols.map((k) =>
      k === "ctr" ? txt(formatMetric(k, data.total?.[k]), XL_BOLD) : n(data.total?.[k], XL_BOLD)
    ),
  ]);

  // Serving locations, appended as further blocks in the same sheet so the
  // whole report travels as one file. Each section is spaced by a blank row and
  // carries its own band, so neither can be mistaken for more day-wise rows.
  const geoCols = data.geo?.columns || [];
  const bandRowIndexes = [];
  const pad = (row) => {
    while (row.length < colCount) row.push(blank());
    return row;
  };

  for (const section of geoSections(data)) {
    if (!section.rows?.length) continue;
    const fields = section.fields || ["site", "geo", "ad"];

    rows.push(pad([blank()]));
    bandRowIndexes.push(rows.length);
    rows.push(pad([txt(`Serving Locations - ${section.label}`, XL_BAND), blank(XL_BAND)]));
    rows.push(
      pad([
        ...fields.map((f) => txt(GEO_FIELD_LABELS[f] || f, XL_BOLD)),
        ...geoCols.map((k) => txt(METRIC_LABELS[k] || k, XL_BOLD)),
      ])
    );
    for (const row of section.rows) {
      rows.push(
        pad([
          ...fields.map((f) => txt(fieldValue(f, row))),
          ...geoCols.map((k) => metricCell(k, row[k])),
        ])
      );
    }
    if (geoCols.length) {
      rows.push(
        pad([
          txt("Total", XL_BOLD),
          ...Array(fields.length - 1).fill(blank(XL_BOLD)),
          ...geoCols.map((k) =>
            k === "ctr"
              ? txt(formatMetric(k, section.total?.[k]), XL_BOLD)
              : n(section.total?.[k], XL_BOLD)
          ),
        ])
      );
    }
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Merge the label/value halves of the 4 header rows and the band row.
  const merges = [];
  for (let r = 0; r <= 4; r++) {
    merges.push({ s: { r, c: 0 }, e: { r, c: 2 } });
    merges.push({ s: { r, c: 3 }, e: { r, c: colCount - 1 } });
  }
  // Stretch each Serving Locations band across its block.
  for (const r of bandRowIndexes) {
    merges.push({ s: { r, c: 0 }, e: { r, c: colCount - 1 } });
  }
  ws["!merges"] = merges;
  ws["!cols"] = [{ wch: 14 }, { wch: 14 }, { wch: 18 }, ...Array(Math.max(metricCols.length, 1)).fill({ wch: 14 })];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Order Report");
  return wb;
}

function OrderReport({ order, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const tableRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API({
          url: "/order_report_metrics/get-report",
          method: "POST",
          responseType: "json",
          data: { order_id: order.order_id },
        });
        if (cancelled) return;
        if (res?.code === 200) {
          setData(res.data);
        } else {
          // services/api.js returns the raw axios response (not .data) on a
          // non-2xx, so a 403 arrives with res.code undefined and the body at
          // res.data.
          Toast("error", res?.data?.message || res?.message || "Could not load the order report");
        }
      } catch (error) {
        console.error("Error loading order report", error);
        if (!cancelled) Toast("error", "Could not load the order report");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.order_id]);

  // Captures the block holding the tables rather than the scroll wrapper around
  // it, so a report wider than the drawer is rendered in full instead of being
  // clipped. Serving Locations sits inside that block and comes along with it.
  const handleDownloadImage = async () => {
    if (!tableRef.current) return;
    setDownloading(true);
    try {
      const blob = await toBlob(tableRef.current, {
        // The drawer has no painted background of its own; without this the
        // PNG comes out with a transparent ground.
        backgroundColor: "#ffffff",
        pixelRatio: 2,
      });
      if (!blob) throw new Error("Empty image blob");
      saveAs(blob, `Order_Report_${safeName(data.order_id)}.png`);
    } catch (error) {
      console.error("Error downloading report image", error);
      Toast("error", "Could not download the report image");
    } finally {
      setDownloading(false);
    }
  };

  // Either block on its own is worth exporting: an order can have Show Geo on
  // with Show Stats off, leaving no day-wise rows but a full location list.
  const hasContent =
    !!(data?.columns?.length && data?.rows?.length) ||
    geoSections(data).some((section) => section.rows?.length);

  const handleExport = () => {
    try {
      const wb = buildWorkbook(data);
      XLSX.writeFile(wb, `Order_Report_${safeName(data.order_id)}.xlsx`);
    } catch (error) {
      console.error("Error exporting order report", error);
      Toast("error", "Could not export the report");
    }
  };

  const renderHeaderBlock = () => {
    const adType = (data.ad_type || []).map(creativeLabel).join(", ");
    const range =
      data.start_date && data.end_date
        ? `${formatDMY(data.start_date)} to ${formatDMY(data.end_date)}`
        : "-";
    return (
      <tr>
        <td style={{ ...CELL, textAlign: "center", fontWeight: 700 }} colSpan={3}>
          <div>Campaign Details:</div>
          <div>Ad Type:</div>
          <div>Date of Initiative:</div>
          <div>Generated Report Range:</div>
        </td>
        <td
          style={{ ...CELL, textAlign: "center", fontWeight: 700 }}
          colSpan={Math.max(data.columns.length, 1)}
        >
          <div>{data.order_name || "-"}</div>
          <div>{adType || "-"}</div>
          <div>{formatDMY(data.start_date)}</div>
          <div>{range}</div>
        </td>
      </tr>
    );
  };

  const renderBody = () => {
    if (!data.columns.length)
      return (
        <tr>
          <td style={{ ...CELL, textAlign: "center" }} colSpan={3}>
            No metrics have been enabled for this report.
          </td>
        </tr>
      );
    if (!data.delivery_available)
      return (
        <tr>
          <td style={{ ...CELL, textAlign: "center" }} colSpan={3 + data.columns.length}>
            Delivery data is temporarily unavailable.
          </td>
        </tr>
      );
    if (!data.campaigns)
      return (
        <tr>
          <td style={{ ...CELL, textAlign: "center" }} colSpan={3 + data.columns.length}>
            No line items on this order yet.
          </td>
        </tr>
      );
    if (!data.rows.length)
      return (
        <tr>
          <td style={{ ...CELL, textAlign: "center" }} colSpan={3 + data.columns.length}>
            No delivery recorded yet.
          </td>
        </tr>
      );

    return (
      <>
        {data.rows.map((row) => (
          <tr key={row.date}>
            <td style={{ ...CELL, textAlign: "center", fontWeight: 600 }}>{row.site}</td>
            <td style={{ ...CELL, textAlign: "center", fontWeight: 600 }}>{formatDMY(row.date)}</td>
            <td style={{ ...CELL, textAlign: "center", fontWeight: 600 }}>
              {creativeLabel(row.ad) || "-"}
            </td>
            {data.columns.map((key) => (
              <td key={key} style={{ ...CELL, textAlign: "center", fontWeight: 600 }}>
                {formatMetric(key, row[key])}
              </td>
            ))}
          </tr>
        ))}
        <tr>
          <td style={{ ...CELL, textAlign: "center", fontWeight: 700 }}>Grand Total</td>
          <td style={{ ...CELL, textAlign: "center", fontWeight: 700 }}>-</td>
          <td style={{ ...CELL, textAlign: "center", fontWeight: 700 }}>-</td>
          {data.columns.map((key) => (
            <td key={key} style={{ ...CELL, textAlign: "center", fontWeight: 700 }}>
              {formatMetric(key, data.total?.[key])}
            </td>
          ))}
        </tr>
      </>
    );
  };

  // Second table: where this order's line items serve. The impressions column
  // is an even split of the order total across those areas, so the server sends
  // it only when the report already publishes impressions.
  const renderGeo = () => {
    const sections = geoSections(data);
    if (!sections.length) return null;

    const geoCols = data.geo?.columns || [];

    return sections.map((section) => {
      const fields = section.fields || ["site", "geo", "ad"];
      const span = fields.length + geoCols.length;

      return (
        <table
          key={section.key}
          style={{ borderCollapse: "collapse", width: "100%", fontSize: "13px", background: "#ffffff" }}
        >
          <tbody>
            <tr>
              <td style={{ ...CELL, ...BAND, textAlign: "center", fontWeight: 700 }} colSpan={span}>
                Serving Locations &mdash; {section.label}
              </td>
            </tr>
            <tr>
              {fields.map((f) => (
                <td key={f} style={{ ...CELL, textAlign: "center", fontWeight: 700 }}>
                  {GEO_FIELD_LABELS[f] || f}
                </td>
              ))}
              {geoCols.map((key) => (
                <td key={key} style={{ ...CELL, textAlign: "center", fontWeight: 700 }}>
                  {METRIC_LABELS[key] || key}
                </td>
              ))}
            </tr>
            {section.rows.map((row, index) => (
              <tr key={`${section.key}-${row.location}-${index}`}>
                {fields.map((f) => (
                  <td key={f} style={{ ...CELL, textAlign: "center", fontWeight: 600 }}>
                    {fieldValue(f, row) || "-"}
                  </td>
                ))}
                {geoCols.map((key) => (
                  <td key={key} style={{ ...CELL, textAlign: "center", fontWeight: 600 }}>
                    {formatMetric(key, row[key])}
                  </td>
                ))}
              </tr>
            ))}
            {geoCols.length ? (
              <tr>
                <td
                  style={{ ...CELL, textAlign: "center", fontWeight: 700 }}
                  colSpan={fields.length}
                >
                  Total
                </td>
                {geoCols.map((key) => (
                  <td key={key} style={{ ...CELL, textAlign: "center", fontWeight: 700 }}>
                    {formatMetric(key, section.total?.[key])}
                  </td>
                ))}
              </tr>
            ) : null}
          </tbody>
        </table>
      );
    });
  };

  return (
    <SlDrawer
      open={order?.order_id ? true : false}
      label={`Order Report - ${order?.order_name || order?.order_id}`}
      style={{ "--size": window.innerWidth <= 767 ? "100vw" : "1000px" }}
      onSlRequestClose={(event) => {
        if (event.detail.source === "overlay") event.preventDefault();
      }}
      onSlAfterHide={(event) => {
        event.stopPropagation();
        onClose();
      }}
    >
      {loading ? (
        <TableLoader />
      ) : !data ? (
        <p className="text-slate-500">Could not load this report.</p>
      ) : !data.configured ? (
        <p className="text-slate-500">
          No report has been configured for this order yet. Ask the CST team to set up Sales
          Report Metrics.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {data.mine === false && localStorage.getItem("role") === roles.ops ? (
            <p className="text-slate-500 text-sm mb-0">
              CST preview &mdash; this is what the salesperson sees.
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleDownloadImage}
              disabled={downloading || !hasContent}
            >
              {downloading ? "Preparing..." : "Download Image"}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleExport}
              disabled={!hasContent}
            >
              Export to Excel
            </button>
          </div>

          <div className="overflow-x-auto">
            <div ref={tableRef} className="flex flex-col gap-4" style={{ background: "#ffffff" }}>
            <table
              style={{ borderCollapse: "collapse", width: "100%", fontSize: "13px", background: "#ffffff" }}
            >
              <tbody>
                {renderHeaderBlock()}
                <tr>
                  <td style={{ ...CELL, ...BAND, textAlign: "center", fontWeight: 700 }} colSpan={3}>
                    Report Fields
                  </td>
                  <td
                    style={{ ...CELL, ...BAND, textAlign: "center", fontWeight: 700 }}
                    colSpan={Math.max(data.columns.length, 1)}
                  >
                    Report Metrics
                  </td>
                </tr>
                <tr>
                  {FIELD_COLUMNS.map((c) => (
                    <td key={c.key} style={{ ...CELL, textAlign: "center", fontWeight: 700 }}>
                      {c.label}
                    </td>
                  ))}
                  {data.columns.map((key) => (
                    <td key={key} style={{ ...CELL, textAlign: "center", fontWeight: 700 }}>
                      {METRIC_LABELS[key] || key}
                    </td>
                  ))}
                </tr>
                {renderBody()}
              </tbody>
            </table>
            {renderGeo()}
            </div>
          </div>

        </div>
      )}
    </SlDrawer>
  );
}

export default OrderReport;
